/**
 * Operaciones → Usuarios (`/operaciones/usuarios`).
 *
 * Puerto de `Users` + `Usuario` + `models/API/Users_model.php` +
 * `views/users/index.php`: la gestión de usuarios del sistema con sus **roles**
 * (`users_groups`) y sus **permisos específicos** (`permissions` +
 * `user_permissions`).
 *
 * Reglas conservadas del panel anterior:
 * - El listado tiene las tres pestañas de estado (Activos / Inactivos / Todos) y
 *   la búsqueda del panel anterior, ahora por nombre, código, DNI, correo,
 *   teléfono o grupo.
 * - Quien no es el usuario 1 no se ve a sí mismo ni al superadmin en la lista, y
 *   los no administradores sólo ven a los griferos (grupo 2).
 * - El formulario es el mismo del alta y de la edición, con las pestañas
 *   **Datos**, **Roles** y **Permisos específicos** (estas dos últimas sólo si el
 *   usuario de la sesión puede asignarlas).
 *
 * Diferencias anotadas:
 * - La API exige el permiso específico de cada acción (el panel anterior sólo
 *   ocultaba botones) y no deja desactivar el propio usuario.
 * - La consulta de DNI a `consulta.api-peru.com` (botón «Buscar» del formulario
 *   anterior) no se migró: depende de un servicio externo con token fijo.
 */

import {
  ArrowLeft,
  Camera,
  Pencil,
  Plus,
  Power,
  ScanFace,
  Search,
  ShieldAlert,
  UserCheck,
  UserX,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatDateTimeValue } from '@/lib/utils';

import { BiometriaUsuarioDialogo } from '../components/biometria-usuario-dialogo';
import { UsuarioDialogo } from '../components/usuario-dialogo';
import { urlFotoUsuario, useCatalogosUsuarios, useMutarUsuarios, useUsuarios } from '../usuarios.api';
import type { Usuario, UsuarioPayload } from '../usuarios.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

const PESTANAS = [
  { clave: '1', etiqueta: 'Activos', icono: UserCheck },
  { clave: '0', etiqueta: 'Inactivos', icono: UserX },
  { clave: '', etiqueta: 'Todos', icono: Users },
] as const;

/** Avatar del usuario (o marcador si no tiene foto o no se puede leer). */
function Avatar({ usuario }: { usuario: Usuario }) {
  const [fallo, setFallo] = useState(false);

  if (!usuario.foto || fallo) {
    return (
      <span
        aria-hidden="true"
        className="flex size-8 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700 ring-1 ring-brand-100"
      >
        {usuario.nombre.charAt(0)}
        {usuario.apellidos.charAt(0)}
      </span>
    );
  }

  return (
    <img
      src={urlFotoUsuario(usuario.id, usuario.foto)}
      alt={`Foto de ${usuario.nombre_completo}`}
      className="size-8 rounded-full object-cover ring-1 ring-ink-100"
      onError={() => setFallo(true)}
    />
  );
}

export function UsuariosPage() {
  useDocumentTitle('Operaciones · Usuarios');

  const [estado, setEstado] = useState<string>('1');
  const [buscar, setBuscar] = useState('');
  const [dialogo, setDialogo] = useState<{ abierto: boolean; id: number | null }>({
    abierto: false,
    id: null,
  });
  const [porCambiar, setPorCambiar] = useState<Usuario | null>(null);
  const [porCara, setPorCara] = useState<Usuario | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);

  const usuarios = useUsuarios(estado, buscar);
  const catalogos = useCatalogosUsuarios();
  const mutaciones = useMutarUsuarios();

  const sinPermiso = usuarios.error instanceof ApiError && usuarios.error.status === 403;
  const lista = usuarios.data?.usuarios ?? [];
  const paginacion = usePaginacion(lista, { clave: [estado, buscar] });
  const puede = usuarios.data?.permisos ?? catalogos.data?.puede ?? null;

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/operaciones"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Operaciones
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Users className="size-6 text-brand-500" aria-hidden="true" />
          Usuarios
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Personal del sistema: sus datos, los roles que le dan acceso y los permisos específicos
          que habilitan acciones dentro de las pantallas.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {usuarios.data ? (
          <Badge variant="outline">
            {usuarios.data.activos} activos · {usuarios.data.inactivos} inactivos
          </Badge>
        ) : null}
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          disabled={sinPermiso || !puede?.crear}
          title={puede?.crear === false ? 'No tienes el permiso «usuarios.crear»' : 'Nuevo usuario'}
          onClick={() => {
            setErrorDialogo(null);
            setDialogo({ abierto: true, id: null });
          }}
        >
          Nuevo usuario
        </Button>
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es de los grupos <strong>admin</strong> y <strong>users</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const cambiarEstado = () => {
    if (!porCambiar) return;

    mutaciones.cambiarEstado.mutate(
      { id: porCambiar.id, activo: !porCambiar.activo },
      {
        onSuccess: (datos) => {
          setPorCambiar(null);
          setAviso({ tono: 'success', texto: datos.mensaje });
        },
        onError: (error) => {
          setPorCambiar(null);
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {puede && !puede.crear && !puede.editar ? (
        <Alert variant="warning" title="Sólo consulta">
          No tienes permisos específicos en esta pantalla, así que sólo puedes ver el listado.
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Listado de usuarios</CardTitle>
          <Badge variant="neutral">{usuarios.data?.total ?? 0} en la vista</Badge>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="flex gap-1 rounded-control bg-ink-50 p-1">
              {PESTANAS.map((pestana) => (
                <button
                  key={pestana.clave || 'todos'}
                  type="button"
                  className={cn(
                    'flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-xs font-semibold transition-colors',
                    estado === pestana.clave
                      ? 'bg-white text-brand-700 shadow-soft'
                      : 'text-ink-500 hover:text-ink-700',
                  )}
                  onClick={() => setEstado(pestana.clave)}
                >
                  <pestana.icono className="size-3.5" aria-hidden="true" />
                  {pestana.etiqueta}
                  {usuarios.data ? (
                    <span className="text-[10px] text-ink-400">
                      {pestana.clave === '1'
                        ? usuarios.data.activos
                        : pestana.clave === '0'
                          ? usuarios.data.inactivos
                          : usuarios.data.disponibles}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>

            <Input
              containerClassName="min-w-[16rem] flex-1"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              placeholder="Buscar por nombre, código, DNI, correo, teléfono o grupo…"
              value={buscar}
              onChange={(evento) => setBuscar(evento.target.value)}
            />
          </div>

          {usuarios.isLoading ? (
            <Skeleton className="h-80 w-full" />
          ) : usuarios.error ? (
            <Alert variant="error" title="No se pudieron leer los usuarios">
              {mensajeDe(usuarios.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay usuarios que coincidan con el filtro actual.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[62rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="w-12 pb-2 pr-3">Foto</th>
                    <th className="pb-2 pr-3">Nombre</th>
                    <th className="w-28 pb-2 pr-3">Código</th>
                    <th className="w-28 pb-2 pr-3">Tipo</th>
                    <th className="w-44 pb-2 pr-3">Contacto</th>
                    <th className="w-32 pb-2 pr-3">Estado</th>
                    <th className="w-28 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginacion.visibles.map((usuario) => (
                    <tr key={usuario.id} className="border-t border-ink-100">
                      <td className="py-2 pr-3">
                        <Avatar usuario={usuario} />
                      </td>
                      <td className="py-2 pr-3">
                        <span
                          className="block max-w-[16rem] truncate font-medium text-ink-900"
                          title={usuario.nombre_completo}
                        >
                          {usuario.nombre_completo}
                        </span>
                        <span className="block font-mono text-[11px] text-ink-400">
                          {usuario.dni ? `DNI ${usuario.dni}` : 'sin DNI'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 font-mono text-xs text-ink-700">
                        {usuario.codigo}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant={usuario.es_admin ? 'brand' : 'outline'}>
                          {usuario.es_admin ? 'Administrador' : 'Grifero'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">
                        {usuario.email ? (
                          <span
                            className="block max-w-[13rem] truncate text-xs text-ink-600"
                            title={usuario.email}
                          >
                            {usuario.email}
                          </span>
                        ) : (
                          <span className="block text-xs text-ink-400">sin correo</span>
                        )}
                        <span className="block whitespace-nowrap text-[11px] text-ink-500">
                          {usuario.telefono ? `Cel. ${usuario.telefono}` : 'sin celular'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3">
                        <Badge variant={usuario.activo ? 'success' : 'neutral'}>
                          {usuario.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <span className="mt-0.5 block text-[10px] text-ink-400">
                          {usuario.ultimo_acceso
                            ? formatDateTimeValue(usuario.ultimo_acceso)
                            : 'nunca entró'}
                        </span>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${usuario.nombre_completo}`}
                            title={puede?.editar ? 'Editar' : 'Sin permiso para editar'}
                            disabled={!puede?.editar}
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, id: usuario.id });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Foto de ${usuario.nombre_completo}`}
                            title="Cambiar foto"
                            disabled={!puede?.editar}
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, id: usuario.id });
                            }}
                          >
                            <Camera className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Cara de ${usuario.nombre_completo}`}
                            title={
                              puede?.editar
                                ? 'Registrar o borrar su cara (acceso con la cámara)'
                                : 'Sin permiso para editar'
                            }
                            disabled={!puede?.editar}
                            onClick={() => {
                              setAviso(null);
                              setPorCara(usuario);
                            }}
                          >
                            <ScanFace className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={
                              usuario.activo
                                ? `Desactivar ${usuario.nombre_completo}`
                                : `Activar ${usuario.nombre_completo}`
                            }
                            className={usuario.activo ? 'text-ink-500' : 'text-brand-600'}
                            disabled={!puede?.cambiar_estado || usuario.es_superadmin}
                            title={
                              usuario.es_superadmin
                                ? 'El usuario principal no se puede desactivar'
                                : puede?.cambiar_estado
                                  ? usuario.activo
                                    ? 'Desactivar'
                                    : 'Activar'
                                  : 'Sin permiso para cambiar el estado'
                            }
                            onClick={() => setPorCambiar(usuario)}
                          >
                            <Power className="size-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lista.length > 0 ? <Paginador {...paginacion} etiqueta="usuarios" className="mt-3" /> : null}

          <Alert variant="info" className="mt-4">
            Desactivar un usuario <strong>no borra</strong> nada: pierde el acceso al sistema y sus
            turnos y ventas se conservan. Los roles y permisos se reemplazan por completo al
            guardar.
          </Alert>
        </CardContent>
      </Card>

      {dialogo.abierto && catalogos.data && puede ? (
        <UsuarioDialogo
          key={`usuario-dialogo-${dialogo.id ?? 'nuevo'}`}
          usuarioId={dialogo.id}
          catalogos={catalogos.data}
          puede={puede}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={(payload: UsuarioPayload, archivo) => {
            setErrorDialogo(null);

            const alTerminar = (id: number, mensaje: string) => {
              if (!archivo) {
                setDialogo({ abierto: false, id: null });
                setAviso({ tono: 'success', texto: mensaje });
                return;
              }

              mutaciones.subirFoto.mutate(
                { id, archivo },
                {
                  onSuccess: () => {
                    setDialogo({ abierto: false, id: null });
                    setAviso({ tono: 'success', texto: `${mensaje} Foto actualizada.` });
                  },
                  onError: (error) =>
                    setErrorDialogo(
                      `Los datos se guardaron, pero la foto falló: ${mensajeDe(error, 'error desconocido')}`,
                    ),
                },
              );
            };

            if (dialogo.id === null) {
              mutaciones.crear.mutate(payload, {
                onSuccess: (datos) => alTerminar(datos.usuario.id, datos.mensaje),
                onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo crear el usuario.')),
              });
              return;
            }

            mutaciones.actualizar.mutate(
              { id: dialogo.id, payload },
              {
                onSuccess: (datos) => alTerminar(datos.usuario.id, datos.mensaje),
                onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo actualizar el usuario.')),
              },
            );
          }}
          onCerrar={() => setDialogo({ abierto: false, id: null })}
        />
      ) : null}

      {dialogo.abierto && catalogos.isLoading ? (
        <Skeleton className="fixed inset-x-0 top-1/3 mx-auto h-40 w-96" />
      ) : null}

      {porCara ? (
        <BiometriaUsuarioDialogo
          key={`biometria-${porCara.id}`}
          usuarioId={porCara.id}
          usuarioNombre={porCara.nombre_completo}
          puedeEditar={Boolean(puede?.editar)}
          onCerrar={() => setPorCara(null)}
          onAviso={(texto) => setAviso({ tono: 'success', texto })}
        />
      ) : null}

      <ConfirmDialog
        open={porCambiar !== null}
        title={porCambiar?.activo ? '¿Desactivar el usuario?' : '¿Activar el usuario?'}
        description={
          porCambiar?.activo
            ? `«${porCambiar?.nombre_completo ?? ''}» no podrá entrar al sistema. Sus datos, turnos y ventas se conservan.`
            : `«${porCambiar?.nombre_completo ?? ''}» volverá a poder entrar al sistema.`
        }
        confirmLabel={porCambiar?.activo ? 'Sí, desactivar' : 'Sí, activar'}
        loading={mutaciones.cambiarEstado.isPending}
        onClose={() => setPorCambiar(null)}
        onConfirm={cambiarEstado}
      />
    </div>
  );
}
